/**
 * Fonctions de calcul pour le curseur de modalité de paiement
 * 
 * Ces fonctions implémentent la logique métier du curseur qui permet
 * au client de simuler différentes configurations d'équipe.
 * 
 * Règles métier :
 * - Le curseur contrôle le nombre de développeurs (1 → 4, par paliers de 0.2)
 * - Valeur par défaut : 2 développeurs
 * - Durée = totalDays / (nbDevs × 18,33 jours/mois)
 * - Mensualité = totalCost / durée
 * - Invariant : mensualité × durée = totalCost (le coût total reste constant)
 * 
 * Formules mensualité par mode :
 * - À la carte : (nbDevs × 18j × 700€) + (nbDevs × 5j × 1000€) = nbDevs × 17 600€
 * - Pack 1 an : (nbDevs × 18j × 588€) + (nbDevs × 5j × 748€) + 500€
 * - Pack 2+ ans : (nbDevs × 18j × 532€) + (nbDevs × 5j × 750.8€) + 500€
 *
 * Composition des coûts (tous modes) :
 * - Développeurs : proportionnel (nbDevs × 18j × TJM)
 * - Chef de projet + Design-MOE : proportionnel (nbDevs × 5j × TJM_MOE)
 * - Expert IA : fixe 500€/mois (Packs uniquement, pas "à la carte")
 */

import type { PackAilixConfig } from '@/utils/packAilixPricing';
import {
  calculateHybridMonthlyPrestationsCost,
  calculateMonthlyPrestationsCost,
  getComparisonModel,
  getTjmForMode as getPackTjm,
} from '@/utils/packAilixPricing';

export type PricingMode = 'a_la_carte' | 'pack_ailix_1an' | 'pack_ailix_2ans';

/**
 * Constantes pour le curseur de modalité de paiement
 */
export const SLIDER_CONSTANTS = {
  // Paliers du curseur
  MIN_DEVS: 1,
  MAX_DEVS: 4,
  STEP_DEVS: 0.2,
  DEFAULT_DEVS: 2,
  
  // Base de calcul
  DAYS_PER_MONTH_PER_DEV: 18.33, // Moyenne observée de capacité développeur (jours/mois)
  
  // TJM par mode (développeurs uniquement)
  TJM_A_LA_CARTE: 700,
  TJM_PACK_1AN: 588,
  TJM_PACK_2ANS: 532,
  
  // Expert IA - coût fixe mensuel (indépendant du nb de devs)
  EXPERT_IA_MONTHLY_COST: 500,
  
  // MOE (Chef de projet + Maître d'œuvre + Designer) - PROPORTIONNEL au nb de devs
  // Base : 5 jours par développeur (10 jours pour 2 devs)
  MOE_DAYS_PER_DEV: 5,
  
  // TJM MOE par mode (Chef de projet + Design-MOE)
  // À la carte : TJM MOE = 1000€ (valeur marché)
  // Pack 1 an : TJM MOE = 748€
  // Pack 2 ans : TJM MOE = 750.8€
  TJM_MOE_A_LA_CARTE: 1000,
  TJM_MOE_1AN: 748,
  TJM_MOE_2ANS: 750.8,
  
  // Durée minimale de projet
  MIN_DURATION_MONTHS: 2,
} as const;

/**
 * Calcule la durée du projet en mois pour un nombre de développeurs donné
 * 
 * @param totalDays - Nombre total de jours de développement
 * @param nbDevs - Nombre de développeurs
 * @returns Durée en mois (arrondie à 0.5 près, minimum 2 mois)
 * 
 * @example
 * calculateSliderDuration(360, 2) // → 10 mois (360 / (2 × 18))
 */
export function calculateSliderDuration(totalDays: number, nbDevs: number): number {
  if (nbDevs <= 0 || totalDays <= 0) {
    return SLIDER_CONSTANTS.MIN_DURATION_MONTHS;
  }
  
  const { DAYS_PER_MONTH_PER_DEV, MIN_DURATION_MONTHS } = SLIDER_CONSTANTS;
  
  // Durée exacte = totalDays / (nbDevs × 18,33 jours/mois)
  const exactDuration = totalDays / (nbDevs * DAYS_PER_MONTH_PER_DEV);
  
  // Arrondir à 0.5 mois près
  const roundedDuration = Math.round(exactDuration * 2) / 2;
  
  // Appliquer le minimum de 2 mois
  return Math.max(MIN_DURATION_MONTHS, roundedDuration);
}

/**
 * Calcule la mensualité pour un coût total et une durée donnés
 * 
 * @param totalCost - Coût total du projet
 * @param duration - Durée en mois
 * @returns Mensualité arrondie à l'euro près
 * 
 * @example
 * calculateSliderMonthlyPayment(100000, 10) // → 10 000€
 */
export function calculateSliderMonthlyPayment(totalCost: number, duration: number): number {
  if (duration <= 0 || totalCost <= 0) {
    return 0;
  }
  
  return Math.round(totalCost / duration);
}

/**
 * Calcule le coût mensuel selon le mode de tarification
 * 
 * Formules :
 * - À la carte : (nbDevs × 18j × 700€) + (nbDevs × 5j × 1000€)
 * - Pack 1 an : (nbDevs × 18j × 588€) + (nbDevs × 5j × 748€) + 500€
 * - Pack 2+ ans : (nbDevs × 18j × 532€) + (nbDevs × 5j × 750.8€) + 500€
 *
 * Composition des coûts (tous modes) :
 * - Développeurs : proportionnel (nbDevs × 18j × TJM)
 * - Chef de projet + Design-MOE : proportionnel (5j par dev × TJM_MOE)
 * - Expert IA : fixe 500€/mois (Packs uniquement)
 * 
 * @param nbDevs - Nombre de développeurs
 * @param mode - Mode de tarification
 * @returns Coût mensuel en euros
 * 
 * @example
 * calculateMonthlyCostForMode(2, 'a_la_carte') // → 35 200€ (25 200 devs + 10 000 MOE)
 * calculateMonthlyCostForMode(2, 'pack_ailix_1an') // → 28 668€
 * calculateMonthlyCostForMode(2, 'pack_ailix_2ans') // → 26 652€
 */
/**
 * @important Caller MUST sanitize `config` via `isHybridActiveForScope()` before
 * passing it. When the scope is below the pack eligibility threshold (< 220 days),
 * pass a config with `comparison_model: 'legacy'` to avoid hybrid prestations.
 */
export function calculateMonthlyCostForMode(
  nbDevs: number,
  mode: PricingMode,
  config?: PackAilixConfig | null,
  enabledPrestations?: Record<string, boolean>,
  clientTjm?: number,
  clientTjmMoe?: number,
): number {
  // Si config fourni → calcul dynamique via prestations
  if (config) {
    const { DAYS_PER_MONTH_PER_DEV } = SLIDER_CONSTANTS;
    const tjm = getPackTjm(mode, config, clientTjm);
    const devCost = nbDevs * DAYS_PER_MONTH_PER_DEV * tjm;
    const prestaCost = getComparisonModel(config) === 'hybrid'
      ? calculateHybridMonthlyPrestationsCost(mode, nbDevs, config, clientTjmMoe, enabledPrestations)
      : calculateMonthlyPrestationsCost(mode, nbDevs, config, enabledPrestations);
    return devCost + prestaCost;
  }

  const {
    DAYS_PER_MONTH_PER_DEV,
    TJM_A_LA_CARTE,
    TJM_PACK_1AN,
    TJM_PACK_2ANS,
    EXPERT_IA_MONTHLY_COST,
    MOE_DAYS_PER_DEV,
    TJM_MOE_A_LA_CARTE,
    TJM_MOE_1AN,
    TJM_MOE_2ANS
  } = SLIDER_CONSTANTS;

  // Jours développeurs par mois
  const devDaysPerMonth = nbDevs * DAYS_PER_MONTH_PER_DEV;
  // Jours MOE (Chef de projet + Design-MOE) par mois - proportionnel
  const moeDaysPerMonth = nbDevs * MOE_DAYS_PER_DEV;
  
  switch (mode) {
    case 'a_la_carte':
      // Développeurs + MOE obligatoire (gestion projet + design-moe) - PAS d'expert IA
      return (devDaysPerMonth * TJM_A_LA_CARTE) + (moeDaysPerMonth * TJM_MOE_A_LA_CARTE);
    
    case 'pack_ailix_1an':
      // Développeurs + MOE proportionnel + Expert IA fixe
      return (devDaysPerMonth * TJM_PACK_1AN) + (moeDaysPerMonth * TJM_MOE_1AN) + EXPERT_IA_MONTHLY_COST;
    
    case 'pack_ailix_2ans':
      // Développeurs + MOE proportionnel + Expert IA fixe
      return (devDaysPerMonth * TJM_PACK_2ANS) + (moeDaysPerMonth * TJM_MOE_2ANS) + EXPERT_IA_MONTHLY_COST;
    
    default:
      // Fallback sécurisé vers "à la carte"
      console.warn(`[calculateMonthlyCostForMode] Mode inconnu: "${mode}", fallback vers a_la_carte`);
      return (devDaysPerMonth * TJM_A_LA_CARTE) + (moeDaysPerMonth * TJM_MOE_A_LA_CARTE);
  }
}

/**
 * Génère la liste des paliers de développeurs disponibles
 * 
 * @returns Tableau des paliers [1, 1.2, 1.4, ..., 4]
 */
export function generateDeveloperPaliers(): number[] {
  const { MIN_DEVS, MAX_DEVS, STEP_DEVS } = SLIDER_CONSTANTS;
  const paliers: number[] = [];
  
  // Utiliser un compteur entier pour éviter les erreurs de floating point
  const numSteps = Math.round((MAX_DEVS - MIN_DEVS) / STEP_DEVS) + 1;
  
  for (let i = 0; i < numSteps; i++) {
    const devs = MIN_DEVS + (i * STEP_DEVS);
    // Arrondir pour éviter les erreurs de floating point (1.0000000001)
    paliers.push(Math.round(devs * 10) / 10);
  }
  
  return paliers;
}

/**
 * Résultat du calcul pour un palier de développeurs
 */
export interface SliderPalierValues {
  /** Nombre de développeurs */
  developers: number;
  /** Durée du projet en mois */
  duration: number;
  /** Mensualité calculée (totalCost / duration) */
  monthlyPayment: number;
  /** Coût mensuel selon le mode de tarification */
  monthlyCostForMode: number;
  /** Indique si c'est le palier par défaut (2 devs) */
  isDefault: boolean;
}

/**
 * Calcule toutes les valeurs pour un palier de développeurs donné
 * 
 * @param totalDays - Nombre total de jours de développement
 * @param totalCost - Coût total du projet
 * @param nbDevs - Nombre de développeurs
 * @param mode - Mode de tarification
 * @returns Objet contenant toutes les valeurs calculées
 * 
 * @example
 * calculateSliderValuesForPalier(400, 280000, 2, 'a_la_carte')
 * // → { developers: 2, duration: 10, monthlyPayment: 28000, monthlyCostForMode: 28000, isDefault: true }
 */
export function calculateSliderValuesForPalier(
  totalDays: number,
  totalCost: number,
  nbDevs: number,
  mode: PricingMode
): SliderPalierValues {
  const duration = calculateSliderDuration(totalDays, nbDevs);
  const monthlyPayment = calculateSliderMonthlyPayment(totalCost, duration);
  const monthlyCostForMode = calculateMonthlyCostForMode(nbDevs, mode);
  
  return {
    developers: nbDevs,
    duration,
    monthlyPayment,
    monthlyCostForMode,
    isDefault: nbDevs === SLIDER_CONSTANTS.DEFAULT_DEVS,
  };
}

/**
 * Pré-calcule toutes les valeurs pour tous les paliers
 * Utilisé pour le lookup instantané lors du déplacement du curseur
 * 
 * @param totalDays - Nombre total de jours de développement
 * @param totalCost - Coût total du projet
 * @param mode - Mode de tarification
 * @returns Map indexée par nombre de développeurs
 */
export function generateAllSliderValues(
  totalDays: number,
  totalCost: number,
  mode: PricingMode
): Map<number, SliderPalierValues> {
  const paliers = generateDeveloperPaliers();
  const values = new Map<number, SliderPalierValues>();
  
  for (const nbDevs of paliers) {
    values.set(nbDevs, calculateSliderValuesForPalier(totalDays, totalCost, nbDevs, mode));
  }
  
  return values;
}

/**
 * Trouve le palier de développeurs le plus proche d'une valeur donnée
 * 
 * @param targetDevs - Valeur cible
 * @returns Palier le plus proche
 */
export function findClosestPalier(targetDevs: number): number {
  const paliers = generateDeveloperPaliers();
  
  let closest = paliers[0];
  let minDiff = Math.abs(paliers[0] - targetDevs);
  
  for (const palier of paliers) {
    const diff = Math.abs(palier - targetDevs);
    if (diff < minDiff) {
      minDiff = diff;
      closest = palier;
    }
  }
  
  return closest;
}
